<div class="flex flex-col gap-6">
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <app-event-kpi-card
      [title]="'pages.settings.userManagement.users.kpis.seatsUsed.title' | translate"
      [infoMessage]="'pages.settings.userManagement.users.kpis.seatsUsed.info' | translate"
      [currentValue]="seatsUsed()"
      [totalValue]="seatsMax()"
      [enableProgressBar]="seatsMax() !== null"
      [loading]="seatsLoading()"
    />

    <app-event-kpi-card
      [title]="'pages.settings.userManagement.users.kpis.pendingInvites.title' | translate"
      [infoMessage]="'pages.settings.userManagement.users.kpis.pendingInvites.info' | translate"
      [currentValue]="pendingInvitesCount()"
      [loading]="pendingInvitesLoading()"
    />
  </div>

  <div class="flex justify-end">
    @if (isInviteButtonLoading()) {
      <ngx-skeleton-loader class="h-11" [theme]="{ height: '44px', width: '148px', 'border-radius': '100vw' }" />
    } @else {
      <app-create-entity-button
        [labelKey]="'pages.settings.userManagement.users.inviteUser'"
        [onClick]="openInviteDialog.bind(this)"
        [permissions]="[EPermissions.campaignSubUserInvitationCreate]"
      />
    }
  </div>

  <div class="users-datatable-container" data-testid="user-management-users-datatable">
    <app-datatable
      [rowData]="filteredRows()"
      [columns]="datatableColumns"
      [configurationInput]="datatableConfig"
      [customColumnDef]="[
        { name: 'name', isSortable: true, template: userColumn },
        { name: 'roleName', isSortable: true, template: roleColumn },
        { name: 'teamName', isSortable: false, template: teamColumn },
        { name: 'status', isSortable: true, template: statusColumn },
        { name: 'actions', isSortable: false, template: actionsColumn },
      ]"
      [total]="filteredRows().length"
      [loaderConfig]="skeletonLoaderConfig"
      [isInitialDataLoaded]="allRows() !== null"
      (dataRefreshRequest)="onDataRefresh($event)"
    />
  </div>

  <ng-template #userColumn let-row="row" let-index="index">
    <div class="flex items-center gap-2 min-w-0">
      <app-icon-badge
        alt="User"
        fallbackBackgroundColor="var(--shikenso-gray-base)"
        [content]="row.icon"
        [fallbackText]="row.name ?? row.email"
        [borderRadius]="100"
        [size]="40"
        [iconSize]="40"
        [fontSize]="16"
      />

      <div class="flex flex-col min-w-0">
        <span class="font-bold line-clamp-1 text-ellipsis">{{ row.name ?? row.email }}</span>

        @if (row.name) {
          <span class="text-xs text-shikenso-purple-base-60 line-clamp-1 text-ellipsis">{{ row.email }}</span>
        }
      </div>
    </div>
  </ng-template>

  <ng-template #roleColumn let-row="row">
    @let roleChangeBlockedMessage = getRoleChangeBlockedMessage(row);
    @let isExpiredInvitation = row.status === 'expired';

    <!-- Tooltip goes on this wrapping span, not the button itself - a disabled button doesn't
         reliably fire the hover events MatTooltip needs. An expired invitation natively disables
         the button (looks greyed out); every other blocking reason (permissions, last admin) keeps
         the normal look but is inert via pointer-events-none/cursor-default. -->
    <span
      class="inline-block"
      matTooltipPosition="above"
      [matTooltip]="roleChangeBlockedMessage ?? ''"
      [matTooltipDisabled]="!roleChangeBlockedMessage"
    >
      <button
        mat-flat-button
        color="secondary-pink"
        class="mat-tiny-plus-button text-xs !bg-shikenso-pink-100-24 !pl-1"
        [disabled]="isExpiredInvitation"
        [class.pointer-events-none]="!isExpiredInvitation && !!roleChangeBlockedMessage"
        [class.!cursor-default]="!isExpiredInvitation && !!roleChangeBlockedMessage"
        (click)="$event.stopPropagation(); onChangeRoleClick(row)"
      >
        <div class="flex items-center gap-2">
          <app-icon-badge
            alt=""
            color="var(--shikenso-purple-base)"
            backgroundColor="var(--shikenso-pink-100-40)"
            [content]="getRoleIconForRoleId(row.roleId)"
            [size]="20"
            [iconSize]="12"
            [borderRadius]="100"
          />
          {{ row.roleName }}
        </div>
      </button>
    </span>
  </ng-template>

  <ng-template #teamColumn let-row="row">
    @let teamChangeBlockedMessage = getTeamChangeBlockedMessage(row);
    @let isExpiredInvitationForTeam = row.status === 'expired';

    <!-- Tooltip goes on this wrapping span, not the button itself - a disabled button doesn't
         reliably fire the hover events MatTooltip needs. An expired invitation natively disables
         the button (looks greyed out); a permission-blocked row keeps the normal look but is
         inert via pointer-events-none/cursor-default - same treatment as the role pill above. -->
    <span
      class="inline-block"
      matTooltipPosition="above"
      [matTooltip]="teamChangeBlockedMessage ?? ''"
      [matTooltipDisabled]="!teamChangeBlockedMessage"
    >
      <button
        mat-flat-button
        color="secondary-gray"
        class="mat-tiny-plus-button text-xs"
        [class.!pl-1]="row.teamName"
        [disabled]="isExpiredInvitationForTeam"
        [class.pointer-events-none]="!isExpiredInvitationForTeam && !!teamChangeBlockedMessage"
        [class.!cursor-default]="!isExpiredInvitationForTeam && !!teamChangeBlockedMessage"
        (click)="$event.stopPropagation(); onChangeTeamClick(row)"
      >
        <div class="flex items-center gap-2">
          @if (row.teamName) {
            <app-icon-badge
              [content]="row.teamIconId"
              [fallbackText]="row.teamName"
              [borderRadius]="100"
              [size]="20"
              [iconSize]="10"
            />
          }

          {{ row.teamName ?? ('pages.settings.userManagement.users.noTeam' | translate) }}
        </div>
      </button>
    </span>
  </ng-template>

  <ng-template #statusColumn let-row="row">
    <div class="relative inline-flex items-center justify-center">
      <app-status-badge
        [status]="row.status"
        colorScheme="invitation"
        [class.opacity-40]="isResendingInvitation(row)"
      />

      @if (isResendingInvitation(row)) {
        <div class="absolute inset-0 flex items-center justify-center">
          <app-dots-spinner />
        </div>
      }
    </div>
  </ng-template>

  <ng-template #actionsColumn let-row="row">
    <div class="flex items-center justify-start">
      <button
        mat-icon-button
        class="mat-secondary-transparent-button-color"
        [matMenuTriggerFor]="menu"
        (click)="$event.stopPropagation()"
      >
        <app-icon src="/icons/three-dots.svg" alt="More" [size]="16" />
      </button>

      <mat-menu #menu="matMenu">
        @let roleChangeBlockedMessage = getRoleChangeBlockedMessage(row);

        <!-- Change Role/Change Team are always rendered (even for expired rows) so the reason
             they're blocked is visible as a tooltip - see getRoleChangeBlockedMessage/
             getExpiredInvitationMessage's doc comments for the priority order. -->
        <span
          class="block"
          matTooltipPosition="above"
          [matTooltip]="roleChangeBlockedMessage ?? ''"
          [matTooltipDisabled]="!roleChangeBlockedMessage"
        >
          <button
            mat-menu-item
            *hasPermission="
              row.status === 'joined' ? EPermissions.userChangeRoleOwn : EPermissions.campaignSubUserInvitationCreate
            "
            [disabled]="!!roleChangeBlockedMessage"
            (click)="onChangeRoleClick(row)"
          >
            {{ 'pages.settings.userManagement.users.changeRole' | translate }}
          </button>
        </span>

        @let expiredInvitationMessageForTeam = getExpiredInvitationMessage(row);

        <span
          class="block"
          matTooltipPosition="above"
          [matTooltip]="expiredInvitationMessageForTeam ?? ''"
          [matTooltipDisabled]="!expiredInvitationMessageForTeam"
        >
          <button
            mat-menu-item
            *hasPermission="
              row.status === 'joined' ? EPermissions.userChangeTeamOwn : EPermissions.campaignSubUserInvitationCreate
            "
            [disabled]="!!expiredInvitationMessageForTeam"
            (click)="onChangeTeamClick(row)"
          >
            {{ 'pages.settings.userManagement.users.changeTeam' | translate }}
          </button>
        </span>

        @if (row.status !== 'joined') {
          <button
            mat-menu-item
            class="relative"
            *hasPermission="EPermissions.campaignSubUserInvitationCreate"
            [disabled]="isInvitationActionInFlight(row)"
            (click)="resendInvitation(row)"
          >
            <span [class.invisible]="isResendingInvitation(row)">
              {{ 'pages.settings.userManagement.users.resendInvitation' | translate }}
            </span>

            @if (isResendingInvitation(row)) {
              <app-dots-spinner [absoluteCentered]="true" />
            }
          </button>
        }

        <!-- Delete deliberately has no expired-status gating - an expired invitation must still be
             deletable so dead rows can be cleaned up (explicit product decision). -->
        @let deleteUserBlockedMessage = getDeleteUserBlockedMessage(row);

        <span
          class="block"
          matTooltipPosition="above"
          [matTooltip]="deleteUserBlockedMessage ?? ''"
          [matTooltipDisabled]="!deleteUserBlockedMessage"
        >
          <button
            mat-menu-item
            *hasPermission="
              row.status === 'joined'
                ? EPermissions.userGroupDeleteMemberOwn
                : EPermissions.campaignSubUserInvitationCreate
            "
            [disabled]="isInvitationActionInFlight(row) || !!deleteUserBlockedMessage"
            (click)="onDeleteClick(row)"
          >
            {{ 'shared.form.delete' | translate }}
          </button>
        </span>
      </mat-menu>
    </div>
  </ng-template>
</div>
